import Network from 'lib/api/network';

export default {
  namespaced: true,

  state() {
    return {
      identity: null,
      apiCredentials: {
        url: null,
        apiKey: null,
      },
      validApiKey: false,
      loading: false,
      validationErrors: null,
    };
  },

  getters: {
    permissionsList() {
      return [
        {
          name: 'Applications',
          fields: {
            get_application: 'Get: Retrieve application',
            list_applications: 'Get: List applications',
          },
        },
        {
          name: 'Candidates',
          fields: {
            get_candidate: 'Get: Retrieve candidate',
            list_candidates: 'Get: List candidates',
          },
        },
        {
          name: 'Custom Fields',
          fields: {
            get_custom_fields: 'Get: Get custom fields',
          },
        },
        {
          name: 'Departments',
          fields: {
            get_department: 'Get: Retrieve department',
            list_departments: 'Get: List departments',
          },
        },
        {
          name: 'Jobs',
          fields: {
            get_job: 'Get: Retrieve job',
            list_jobs: 'Get: List jobs',
            get_hiring_team: 'Get: Get hiring team',
          },
        },
        {
          name: 'Offers',
          fields: {
            get_application_offers: 'Get: List application\'s offers',
            get_application_current_offer: 'Get: Retrieve application\'s current offer',
            get_offer: 'Get: Retrieve offer',
            list_offers: 'Get: List offers',
          },
        },
        {
          name: 'Offices',
          fields: {
            get_office: 'Get: Retrieve office',
            list_offices: 'Get: List offices',
          },
        },
        {
          name: 'Sources',
          fields: {
            list_sources: 'Get: List sources',
          },
        },
        {
          name: 'Users',
          fields: {
            get_user: 'Get: Retrieve user',
            list_users: 'Get: List users',
          },
        },
      ];
    },
  },

  mutations: {
    update(state, { key, value }) {
      state[key] = value;
    },
  },

  actions: {
    track(_context, { eventName, properties }) {
      const fullEventName = `ATS Connect: ${eventName}`;
      const fullProperties = properties || {};
      window.mixpanel.track(fullEventName, fullProperties);
      window.FS.event(fullEventName); // ignore fullProperties
      window.Intercom('trackEvent', fullEventName);
    },

    openConnectModal({ commit, dispatch }) {
      dispatch('track', {
        eventName: 'Open Connect Modal',
      });

      commit('updateModal', {
        visible: true,
        subcomponent: 'ats-connect-modals-auth-url',
      }, { root: true });
    },

    createDirectIntegrationIdentity({ state, commit, dispatch }, modalArgs) {
      dispatch('track', {
        eventName: 'Create Direct Integration Identity',
      });

      state.loading = true;
      const params = {
        direct_integration_identity: {
          integration_name: 'greenhouse',
          api_key: state.apiCredentials.apiKey,
        },
      };
      return new Promise((resolve, reject) => {
        Network.post(
          '/direct_integration_identities',
          {
            ...params,
          },
          {
            success: (response) => {
              state.identity = response.direct_integration_identity;
              commit('updateModal', modalArgs, { root: true });
              dispatch('track', {
                eventName: 'Create Direct Integration Identity',
                properties: {
                  identityId: response.direct_integration_identity.id,
                },
              });
              resolve(response.direct_integration_identity);
            },
            error: (response) => {
              commit('update', { key: 'errors', value: response.errors });
              dispatch('track', {
                eventName: 'Create Direct Integration Identity Failed',
                properties: {
                  errors: response.errors,
                },
              });
              reject();
            },
            complete: () => {
              state.loading = false;
            },
          },
        );
      });
    },

    resetValidationErrors({ state, dispatch }) {
      state.validationErrors = null;
      dispatch('validateApiKey');
    },

    validateApiKey({ state, dispatch }) {
      state.validApiKey = false;
      dispatch('track', {
        eventName: 'Api Key Validation Started',
      });
      return new Promise((resolve, reject) => {
        Network.get(`/direct_integration_identities/${state.identity.id}/validate`, {
          success: (response) => {
            state.identity = response.direct_integration_identity;
            if (['validated', 'active'].includes(response.direct_integration_identity.state)) {
              state.validApiKey = true;
              dispatch('track', {
                eventName: 'Api Key Validation Succeeded',
              });
            }
            resolve(response.direct_integration_identity.state);
          },
          error: () => {
            state.validApiKey = false;
            dispatch('track', {
              eventName: 'Api Key Validation Failed',
            });
            reject();
          },
        });
      });
    },

    pollForIdentityState({ state, dispatch }, statesToPollFor = ['active']) {
      // A `created` state should, theoretically, be extremely short. If the auth succeeded, the identity moves
      // from `created` to `validating` immediately. So here we just assume that a `created` state is an identity
      // that's stuck (which can happen due to an unexpected exception, or due to the user just not finishing the auth).
      if (!state.identity || state.identity.state === 'created') {
        state.loading = false;
        return;
      }

      dispatch('track', {
        eventName: 'Poll For Identity State',
      });

      state.loading = true;
      let timeoutId;
      const poll = () => {
        Network.get(`/direct_integration_identities/${state.identity.id}`, {
          success: (response) => {
            state.identity = response.direct_integration_identity;
            if (statesToPollFor.includes(response.direct_integration_identity.state)) {
              state.validApiKey = true;
              state.loading = false;
              dispatch('track', {
                eventName: 'Poll For Identity State',
                properties: {
                  status: response.direct_integration_identity.state,
                },
              });
            } else if (response.direct_integration_identity.state === 'error') {
              state.loading = false;
              state.validationErrors = response.validation_errors;
              state.validApiKey = false;
            } else {
              timeoutId = setTimeout(poll, 5000);
            }
          },
          error: () => {
            dispatch('track', {
              eventName: 'Poll For Identity State',
              properties: {
                status: 'error',
              },
            });
            state.loading = false;
          },
        });
      };
      poll();
      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    },

    initiateOAuth({ state, commit, dispatch }) {
      if (state.loading) return Promise.resolve();

      dispatch('track', { eventName: 'Initiate OAuth' });
      commit('update', { key: 'loading', value: true });

      return new Promise((resolve, reject) => {
        Network.post(
          '/direct_integration_identities/initiate_oauth',
          {},
          {
            success: (response) => {
              commit('update', { key: 'identity', value: response.direct_integration_identity });
              dispatch('track', {
                eventName: 'OAuth Redirect',
                properties: { identityId: response.direct_integration_identity.id },
              });
              window.location.href = response.authorization_url;
              resolve();
            },
            error: (response) => {
              commit('update', { key: 'errors', value: response.errors });
              dispatch('track', { eventName: 'Initiate OAuth Failed' });
              commit('update', { key: 'loading', value: false });
              reject();
            },
          },
        );
      });
    },

    navigateToEnrollmentPage() {
      window.location.href = '/onboarding/tags/enrollment';
    },
  },
};
